import { useFBO } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import * as React from "react";
import * as THREE from "three";
import { TransitionMaterial } from "./TransitionMaterial";
import { PageTransitionContext } from "./PageTransitionContext";

type Props = {
  /** The page the user wants to see. */
  requestedPage: string;
  /** The page currently rendered. */
  activePage: string;
  /** Called once the snapshot is captured — parent should set activePage = requestedPage. */
  onSwap: () => void;
  children: React.ReactNode;
};

const _forward = new THREE.Vector3(0, 0, -1);
// Scratch vector reused each frame so we don't clone _forward per render.
const _quadDir = new THREE.Vector3();

/**
 * Wraps page content inside the Canvas.
 *
 * Page swap is a 2-FBO shader cross-fade (same pattern as
 * ScrollTransitionScene):
 *   1. Capture the old page to fboPrev on click.
 *   2. While in transition, render the live new page each frame to fboNext —
 *      pageGroup itself is hidden from the default framebuffer.
 *   3. A single full-viewport quad with TransitionMaterial mixes fboPrev →
 *      fboNext via the `progression` uniform.
 * This avoids both the "zoom" overlap (two scenes drawn at once with
 * different camera POVs) and the bright-flash fade-to-clear of the simpler
 * single-FBO opacity approach.
 */
export function PageTransitionOverlay({
  requestedPage,
  activePage,
  onSwap,
  children,
}: Props) {
  const { gl, scene, camera, size } = useThree();
  const fboPrev = useFBO();
  const fboNext = useFBO();

  // Match ScrollTransitionScene's FBO setup — the renderer writes sRGB into
  // these targets, so the shader needs to know to decode them as sRGB or the
  // composite double-encodes gamma and washes the highlights flat (visible as
  // the arch's portal light disappearing).
  React.useLayoutEffect(() => {
    fboPrev.texture.colorSpace = THREE.SRGBColorSpace;
    fboNext.texture.colorSpace = THREE.SRGBColorSpace;
  }, [fboPrev, fboNext]);
  const quadRef = React.useRef<THREE.Mesh>(null);
  const materialRef = React.useRef<InstanceType<typeof TransitionMaterial> | null>(
    null,
  );
  const pageGroupRef = React.useRef<THREE.Group>(null);

  /** 0 = show fboPrev (snapshot), 1 = show fboNext (live new page). */
  const progressionRef = React.useRef(0);
  const needsCapture = React.useRef(false);
  const captured = React.useRef(false);
  /** True once React has committed activePage = requestedPage. */
  const swapCommitted = React.useRef(false);
  /** True while the shader transition is active (pageGroup must stay hidden
   *  from the default framebuffer and rendered to fboNext instead). */
  const transitioning = React.useRef(false);
  /** True once fboNext has been captured (one-shot, on swap commit). */
  const fboNextReady = React.useRef(false);
  /** Frames waited after swap commit before capturing fboNext. Gives the new
   *  page's own internal useFrames (esp. ScrollTransitionScene's fbo1/fbo2)
   *  time to populate before we snapshot the result. */
  const commitFrameCount = React.useRef(0);

  const tmpColor = React.useMemo(() => new THREE.Color(), []);

  // Exposed to descendants so per-frame scale ramping (deck box, fan cards,
  // etc.) can be frozen on BOTH sides of the swap (outgoing /decks AND
  // incoming /decks). Otherwise the user sees content "growing in" behind
  // the fading snapshot.
  const isTransitioningRef = React.useRef(false);
  const ctxValue = React.useMemo(
    () => ({ isTransitioningRef }),
    [],
  );

  React.useEffect(() => {
    if (requestedPage !== activePage) {
      needsCapture.current = true;
      captured.current = false;
      swapCommitted.current = false;
      fboNextReady.current = false;
      commitFrameCount.current = 0;
    } else if (captured.current) {
      swapCommitted.current = true;
    }
  }, [requestedPage, activePage]);

  useFrame((_, delta) => {
    const quad = quadRef.current;
    const mat = materialRef.current;
    const pageGroup = pageGroupRef.current;
    if (!quad || !mat || !pageGroup) return;

    // Step 1: capture the OLD page into fboPrev. While transitioning the
    // live new page renders normally underneath; the snapshot fades over it.
    if (needsCapture.current && !captured.current) {
      quad.visible = false;
      gl.getClearColor(tmpColor);
      const prevAlpha = gl.getClearAlpha();
      gl.setClearColor(tmpColor, 1);
      gl.setRenderTarget(fboPrev);
      gl.clear();
      gl.render(scene, camera);
      gl.setRenderTarget(null);
      gl.setClearColor(tmpColor, prevAlpha);

      needsCapture.current = false;
      captured.current = true;
      progressionRef.current = 0;
      transitioning.current = true;
      isTransitioningRef.current = true;
      mat.uniforms.alpha.value = 1;
      // Both texture slots point at fboPrev — the shader's mix() output is
      // always pure snapshot. We use the material as a single-texture fade
      // via the `alpha` uniform.
      mat.uniforms.tex.value = fboPrev.texture;
      mat.uniforms.tex2.value = fboPrev.texture;
      mat.uniforms.progression.value = 0;
      mat.uniforms.transition.value = 0; // mix mode (single texture so no overlap)
      mat.transparent = true;

      pageGroup.visible = true;
      quad.visible = true;

      onSwap();
    }

    if (transitioning.current) {
      // Step 2: hold snapshot at full alpha until 8 frames after the swap is
      // committed (camera teleport, drei scroll init, ScrollTransitionScene
      // fbos settle), then fade alpha 1 → 0 quickly (~250 ms).
      if (swapCommitted.current) {
        commitFrameCount.current += 1;
        if (commitFrameCount.current >= 8) {
          progressionRef.current = Math.min(
            1,
            progressionRef.current + delta * 4,
          );
        }
      }
      mat.uniforms.alpha.value = 1 - progressionRef.current;

      if (progressionRef.current >= 1) {
        transitioning.current = false;
        captured.current = false;
        commitFrameCount.current = 0;
        isTransitioningRef.current = false;
        quad.visible = false;
      }
    }
  });

  // Position the quad in front of the camera right before it renders. Doing
  // this inside useFrame would race with ScrollSceneController's camera
  // teleport on the first post-swap frame.
  const onQuadBeforeRender = React.useCallback(
    (_gl: THREE.WebGLRenderer, _scene: THREE.Scene, cam: THREE.Camera) => {
      const quad = quadRef.current;
      if (!quad || !quad.visible) return;
      const perspectiveCam = cam as THREE.PerspectiveCamera;
      const dist = 1;
      const vFov = (perspectiveCam.fov * Math.PI) / 180;
      const h = 2 * Math.tan(vFov / 2) * dist;
      const w = h * (size.width / size.height);
      quad.position.copy(perspectiveCam.position);
      quad.quaternion.copy(perspectiveCam.quaternion);
      quad.position.addScaledVector(
        _quadDir.copy(_forward).applyQuaternion(perspectiveCam.quaternion),
        dist,
      );
      quad.scale.set(w, h, 1);
    },
    [size.width, size.height],
  );

  return (
    <PageTransitionContext.Provider value={ctxValue}>
      <group ref={pageGroupRef}>{children}</group>
      <mesh
        ref={quadRef}
        visible={false}
        frustumCulled={false}
        renderOrder={9999}
        onBeforeRender={onQuadBeforeRender}
      >
        <planeGeometry args={[1, 1]} />
        {/* @ts-expect-error transitionMaterial is extended via extend() */}
        <transitionMaterial
          ref={materialRef}
          depthWrite={false}
          depthTest={false}
        />
      </mesh>
    </PageTransitionContext.Provider>
  );
}
